import { useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';

import ADD_ADDITIONAL_DETAILS from 'store/mutations/ADD_ADDITIONAL_DETAILS';
import { subtract } from 'utils/calculator';

import { CREATE_VOUCHER, GET_MANUAL_VOUCHER, GET_MANUAL_VOUCHER_BOOKING } from './graphql';
import { sumAllVoucherValues, voucherAdditionalDetails, vouchersAliasList, vouchersInAdditionalDetails } from './utils';

const VOUCHER_MARKET = 'GB';

const getVouchers = async ({ aliases, client }) => {
  const results = await Promise.allSettled(
    aliases.map((reference) =>
      client.query({
        query: GET_MANUAL_VOUCHER,
        variables: {
          reference,
          market: VOUCHER_MARKET
        },
        fetchPolicy: 'network-only'
      })
    )
  );

  return results
    .filter((result) => result.status === 'fulfilled')
    .map((result) => result.value?.data?.findVoucherByReferenceAndMarket)
    .filter(Boolean);
};

const validateAssociatedPNR = async ({ amount, associatedPNR, client }) => {
  const {
    data: { booking }
  } = await client.query({
    query: GET_MANUAL_VOUCHER_BOOKING,
    variables: {
      reference: associatedPNR
    },
    fetchPolicy: 'network-only'
  });

  const vouchersAssociatedToBooking = vouchersInAdditionalDetails(booking);
  const aliases = vouchersAliasList(vouchersAssociatedToBooking);
  const vouchers = await getVouchers({ aliases, client });
  const totalVoucherAmount = sumAllVoucherValues(vouchers);
  const maxVoucherValue = subtract(booking.totalPrice, totalVoucherAmount);

  if (maxVoucherValue <= 0) {
    throw new Error('Total amount of manual vouchers created exceeds the total price of the booking');
  }

  if (amount > maxVoucherValue) {
    throw new Error(`The e-voucher amount cannot exceed: ${booking.currency}${maxVoucherValue}`);
  }
};

const addVoucherAdditionalDetails = ({ addAdditionalDetails, alias, bookingReference, type }) =>
  addAdditionalDetails({
    variables: {
      bookingReference,
      additionalDetails: [
        {
          key: `${voucherAdditionalDetails[type]}--${alias}`,
          value: `${alias}`
        }
      ]
    }
  });

const useManualVoucherCreation = () => {
  const client = useApolloClient();

  const [createVoucher, { loading: createVoucherLoading, error: createVoucherError }] = useMutation(CREATE_VOUCHER);
  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading, error: addAdditionalDetailsError }] =
    useMutation(ADD_ADDITIONAL_DETAILS);

  const isMounted = useRef(true);
  const [handlingMutation, setHandlingMutation] = useState(false);
  const [error, setError] = useState(undefined);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  useEffect(() => {
    const currentError = createVoucherError ?? addAdditionalDetailsError;

    if (currentError && error !== currentError) {
      setError(currentError);
    }
  }, [addAdditionalDetailsError, createVoucherError, error]);

  const handleMutation = async ({ variables }) => {
    setHandlingMutation(true);
    setError(undefined);

    try {
      if (variables.associatedPNR) {
        await validateAssociatedPNR({
          amount: variables.amount,
          associatedPNR: variables.associatedPNR,
          client
        });
      }

      const response = await createVoucher({ variables });
      const alias = response?.data?.createVoucher?.alias;

      if (variables.associatedPNR) {
        await addVoucherAdditionalDetails({
          addAdditionalDetails,
          alias,
          bookingReference: variables.associatedPNR,
          type: variables.type
        });
      }

      return response;
    } catch (err) {
      if (isMounted.current) {
        setError(err);
      }
      throw err;
    } finally {
      if (isMounted.current) {
        setHandlingMutation(false);
      }
    }
  };

  return [
    handleMutation,
    {
      loading: createVoucherLoading || addAdditionalDetailsLoading || handlingMutation,
      error
    }
  ];
};

export default useManualVoucherCreation;
